(function () {
  'use strict';

  // Video carousel section, integrated with empire's section registry the
  // same way the theme's native Flickity-using sections (dynamic-slideshow,
  // static-subcollections-featured-collection, etc.) are. Empire owns the
  // instance lifecycle: it constructs us on shopify:section:load and calls
  // onSectionUnload before re-rendering or removing the section. That gives
  // us clean tear-down on every editor edit, no leaked autoplay timers, no
  // stacked Flickity instances.

  function VideoCarousel(section) {
    this.id = section.id;
    this.el = section.el; // The shopify-section wrapper
    this.sectionEl = section.el.querySelector('[data-section-type="video-carousel"]');
    this.flkty = null;
    this._listeners = [];
    this._destroyed = false;

    // Defer init to the next frame so the new section's CSS has a chance to
    // resolve. Empire constructs us synchronously inside shopify:section:load
    // — too early for cells that depend on CSS custom-property widths.
    var self = this;
    this._initRaf = requestAnimationFrame(function () {
      self._initRaf = null;
      if (!self._destroyed) self._init();
    });
  }

  VideoCarousel.prototype._init = function () {
    if (!this.sectionEl || !window.Flickity) return;
    var track = this.sectionEl.querySelector('[data-vc-track]');
    if (!track) return;

    // Defensive: if a previous Flickity instance is somehow still attached to
    // this track (e.g. the editor replaced part of the DOM without firing
    // unload), tear it down before we wrap it again.
    if (track.classList.contains('flickity-enabled') && window.Flickity.data) {
      var prior = window.Flickity.data(track);
      if (prior) { try { prior.destroy(); } catch (e) {} }
    }

    var flkty = new window.Flickity(track, {
      cellSelector: '[data-vc-slide]',
      cellAlign: 'left',
      contain: true,
      groupCells: false,
      wrapAround: true,
      autoPlay: 5000,
      pauseAutoPlayOnHover: true,
      pageDots: false,
      prevNextButtons: false,
      draggable: true,
      accessibility: true
    });
    this.flkty = flkty;

    // Re-measure on the next frame in case CSS custom-property widths
    // resolved against a transient parent at init time.
    requestAnimationFrame(function () { flkty.resize(); });

    this._wireControls();
  };

  VideoCarousel.prototype._wireControls = function () {
    var self = this;
    var flkty = this.flkty;
    var section = this.sectionEl;
    var prevBtn = section.querySelector('[data-vc-prev]');
    var nextBtn = section.querySelector('[data-vc-next]');
    var dots = Array.prototype.slice.call(section.querySelectorAll('[data-vc-dot]'));

    function on(el, evt, fn) {
      el.addEventListener(evt, fn);
      self._listeners.push({ el: el, evt: evt, fn: fn });
    }

    if (prevBtn) on(prevBtn, 'click', function () { flkty.previous(); });
    if (nextBtn) on(nextBtn, 'click', function () { flkty.next(); });
    dots.forEach(function (dot) {
      var idx = parseInt(dot.getAttribute('data-vc-dot'), 10) || 0;
      on(dot, 'click', function () { flkty.select(idx); });
    });

    function setActiveDot(idx) {
      dots.forEach(function (dot, i) {
        if (i === idx) dot.classList.add('is-active');
        else dot.classList.remove('is-active');
      });
    }

    function syncDots() {
      var groupCount = (flkty.slides && flkty.slides.length) || dots.length;
      dots.forEach(function (dot, i) {
        var item = dot.parentElement || dot;
        item.style.display = i < groupCount ? '' : 'none';
      });
      setActiveDot(flkty.selectedIndex || 0);
    }
    syncDots();
    flkty.on('select', function () { setActiveDot(flkty.selectedIndex); });
    flkty.on('resize', syncDots);

    var nav = section.querySelector('.video-carousel__nav');
    if (nav && dots.length === 0) nav.style.display = 'none';
  };

  // Empire lifecycle: called before the section is re-rendered or removed.
  VideoCarousel.prototype.onSectionUnload = function () {
    this._destroyed = true;
    if (this._initRaf) {
      cancelAnimationFrame(this._initRaf);
      this._initRaf = null;
    }
    if (this.flkty) {
      try {
        // Stop the Flickity player explicitly — destroy() may leak the
        // autoplay setInterval if the carousel has been detached.
        if (this.flkty.player && typeof this.flkty.player.stop === 'function') {
          this.flkty.player.stop();
        }
        this.flkty.destroy();
      } catch (e) { /* DOM already gone — ignore */ }
      this.flkty = null;
    }
    this._listeners.forEach(function (l) {
      try { l.el.removeEventListener(l.evt, l.fn); } catch (e) {}
    });
    this._listeners = [];
  };

  // Pause autoplay while the merchant has the section open in the editor,
  // so cards don't slide out from under them while they're editing.
  VideoCarousel.prototype.onSectionSelect = function () {
    if (this.flkty && this.flkty.player) this.flkty.player.pause();
  };
  VideoCarousel.prototype.onSectionDeselect = function () {
    if (this.flkty && this.flkty.player) this.flkty.player.unpause();
  };
  // Empire calls these via _onSectionEvent — note the names: onSectionBlock*
  // (not onBlock*). The wrong names worked silently because empire just
  // skips a missing method.
  VideoCarousel.prototype.onSectionBlockSelect = function (data) {
    if (this.flkty && this.flkty.player) this.flkty.player.pause();
    if (!this.flkty || !data || !this.sectionEl) return;

    var slides = this.sectionEl.querySelectorAll('[data-vc-slide]');
    var index = -1;

    // Some Shopify versions pass the block element as data.el, others pass
    // the section wrapper. Cover both.
    if (data.el) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i] === data.el || slides[i].contains(data.el)) {
          index = i;
          break;
        }
      }
    }

    // Fallback: shopify_attributes on the block <li> adds id="shopify-block-<id>"
    // so we can match the slide's id against the event's block id.
    if (index < 0 && data.id) {
      var blockId = String(data.id);
      for (var j = 0; j < slides.length; j++) {
        if (slides[j].id && slides[j].id.indexOf(blockId) !== -1) {
          index = j;
          break;
        }
      }
    }

    if (index >= 0) {
      // Smooth-scroll the carousel so the selected card lands in view.
      this.flkty.select(index, false, false);
    }
  };
  VideoCarousel.prototype.onSectionBlockDeselect = function () {
    if (this.flkty && this.flkty.player) this.flkty.player.unpause();
  };

  function register() {
    var sections = window.theme && window.theme.sections;
    if (!sections) {
      // empire.js hasn't run yet — try again after load.
      window.addEventListener('load', register);
      return;
    }
    if (sections._registered_video_carousel) return;
    sections._registered_video_carousel = true;
    sections.register('video-carousel', function (section) {
      return new VideoCarousel(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', register);
  } else {
    register();
  }
})();
